<script setup>
import { computed } from 'vue';

/**
 * 복지찬스(SmartWel) 진입 행 공통 컴포넌트.
 * - [구독중/구독하기 OR 액션] 좌측 + [P 복지포인트 N P] 우측 정보
 * - 사용처: SmartwelPointBar(모바일 플로팅), SmartwelHeaderEntry(PC 헤더),
 *   DropdownCreditInfo(myMenu 카드), CreditBar(인라인), points.vue(모바일 카드 - cta 모드)
 *
 * Props:
 *  - variant: 'floating' | 'pc-header' | 'dropdown-card' | 'inline'
 *  - point: 복지포인트 잔액 (number)
 *  - subscribed: true=구독중 / false=구독하기 (ctaLabel 없을 때만 의미)
 *  - ctaLabel: 비어있지 않으면 sub-badge 자리에 이 라벨의 액션 버튼 표시
 *              (구독 의미 대신 페이지 컨텍스트 액션 — 예: '복지포인트 전환')
 *  - showExpand: 우측 끝 ▶ 화살표 노출 (floating용)
 *
 * Events:
 *  - click-sub: 좌측 sub-badge 클릭 (ctaLabel 모드든 구독 모드든 공통)
 *  - click-point: 우측 포인트 영역 클릭
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'floating',
    validator: (v) => ['floating', 'pc-header', 'dropdown-card', 'inline'].includes(v),
  },
  point: { type: Number, default: 0 },
  subscribed: { type: Boolean, default: false },
  ctaLabel: { type: String, default: '' },
  showExpand: { type: Boolean, default: false },
});

defineEmits(['click-sub', 'click-point']);

const formattedPoint = computed(() => (props.point ?? 0).toLocaleString());
</script>

<template>
  <div class="swr" :class="`swr--${variant}`">
    <button
      type="button"
      class="swr__sub-badge"
      :class="
        ctaLabel
          ? 'swr__sub-badge--cta'
          : subscribed
          ? 'swr__sub-badge--on'
          : 'swr__sub-badge--off'
      "
      @click="$emit('click-sub')"
    >
      {{ ctaLabel || (subscribed ? '구독중' : '구독하기') }}
    </button>

    <button type="button" class="swr__point" @click="$emit('click-point')">
      <span class="swr__p-badge">P</span>
      <span class="swr__point-label">복지포인트</span>
      <span class="swr__point-value">{{ formattedPoint }}</span>
      <span class="swr__point-unit">P</span>
      <i v-if="showExpand" class="swr__expand" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
/* ===== 공통: 한 묶음 박스 (sub-badge + point 인라인) ===== */
.swr {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--smartwel-blue-softer, #f0f7ff);
  border: 1px solid var(--smartwel-blue-border, #dbeaff);
}

/* 좌측 구독 뱃지 (pill) */
.swr__sub-badge {
  flex-shrink: 0;
  padding: 3px 9px;
  border: none;
  border-radius: 100px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  line-height: 1.2;

  &--on {
    background: var(--smartwel-sub-on-bg, #1e3a5f);
  }
  &--off {
    background: var(--smartwel-sub-off-bg, #d85a30);
  }
  /* ctaLabel 모드: 페이지 컨텍스트 액션 (구독 의미 아님) — orange300 톤으로 강조 */
  &--cta {
    background: var(--orange-300-main, #eb4c10);
  }
}

/* 우측 포인트 영역 (인라인 텍스트, 배경 없음 — 박스는 .swr가 담당) */
.swr__point {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
}

.swr__p-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--smartwel-blue, #3391ff);
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}

.swr__point-label {
  font-size: 12px;
  font-weight: 500;
  color: #333;
}

.swr__point-value {
  margin-top: -2px;
  font-size: 17px;
  font-weight: 600;
  color: var(--smartwel-blue, #3391ff);
  line-height: 1;
}

.swr__point-unit {
  font-size: 12px;
  color: #333;
}

.swr__expand {
  display: inline-block;
  width: 16px;
  height: 16px;
  background: url('@manseImages/icon_dropdown.svg') no-repeat center / contain;
  transform: rotate(-90deg);
  flex-shrink: 0;
}

/* ===== variant 미세 조정 ===== */

/* floating: 외곽 floating-bar 카드(흰 배경)가 박스 역할 → swr는 콘텐츠만 */
.swr--floating {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;

  /* 구독중/구독하기 버튼 텍스트 키움 (floating 전용) */
  .swr__sub-badge {
    font-size: 13px;
  }
}

/* 갤럭시 폴드(닫힘) 등 좁은 화면: floating에서 '복지포인트' 라벨 숨겨 압축 */
@media (max-width: 320px) {
  .swr--floating .swr__point-label {
    display: none;
  }
}

/* pc-header: 헤더 영역 컴팩트 */
.swr--pc-header {
  padding: 4px 8px;
  border-radius: 8px;
}

/* dropdown-card: myMenu 카드 풀폭 (좁은 폭에서 wrap 허용) */
.swr--dropdown-card {
  display: flex;
  width: 100%;
  justify-content: space-between;
  flex-wrap: wrap;

  /* 구독중/구독하기 버튼 텍스트 키움 (drawer) */
  .swr__sub-badge {
    font-size: 12px;
  }
}

/* inline: CreditBar 내부 인라인 — 박스/배경/패딩 없음 + 순서 swap (포인트 먼저, 구독 뱃지 뒤) */
.swr--inline {
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  gap: 12px;

  .swr__point {
    order: 1;
  }
  .swr__sub-badge {
    order: 2;
    font-size: 12px;
  }
  /* 구독하기(--off)만 충전하기와 동일 톤(orange300). 구독중(--on)은 디폴트 남색 유지 → Header와 통일 */
  .swr__sub-badge--off {
    background: var(--orange-300-main, #eb4c10);
  }
  /* CreditBar 분석권/낭만P와 동일 톤 */
  .swr__p-badge {
    width: 18px;
    height: 18px;
    font-size: 12px;
    line-height: 18px;
  }
  .swr__point-label {
    color: var(--Gray-700, #575757);
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
  }
  .swr__point-value {
    margin: 0;
  }
}

</style>
